/**
 * Costumes, newest first (2026-10-20 owner ask: "sort Pikachu costumes by
 * release date, newest to oldest").
 *
 * The registry lists a species' costumes in the order the generator met
 * them, which for Pikachu is a decade of party hats in no order a trainer
 * can predict. The Costume dex and the species page now draw them by GO
 * release date, newest first, so the costume you can still go and catch is
 * the first cell and the 2016 Santa hat is the last.
 *
 * Two sources, in this order:
 *   1. PIKACHU_COSTUME_RELEASED, a hand-kept table of Pikachu's costumes by
 *      form id (the species the ask is about, and the one whose tokens say
 *      the least: FLYING_01, PI, DOCTOR). Dates are the GO debut, from The
 *      Click's costume Pikachu tracker cross-checked against the event
 *      announcements; a handful are month-precise (marked), which is all a
 *      sort needs.
 *   2. The asset token for everything else: PokeMiners names a costume
 *      file after its event ("pm1.fFALL_2019", "pm4.cHOLIDAY_2022"), so a
 *      year and a season or month come straight off the file name. A token
 *      with no year (the Ash caps, which are PokéAPI forms) has no date and
 *      sorts last, in registry order.
 *
 * Pure, no DOM.
 */

/** Pikachu's costumes by form id: the GO debut, YYYY-MM-DD local. */
export const PIKACHU_COSTUME_RELEASED: Readonly<Record<string, string>> = {
  "pikachu-holiday-2016": "2016-12-12",
  "pikachu-costume-1": "2017-02-26", // party hat, Pokémon Day 2017 (month-precise)
  "pikachu-one-year-anniversary": "2017-07-06",
  "pikachu-costume-2": "2017-07-06", // (month-precise)
  "pikachu-halloween-2017": "2017-10-20",
  "pikachu-anniversary": "2018-02-26", // party hat, Pokémon Day 2018 (month-precise)
  "pikachu-summer-2018": "2018-07-05",
  "pikachu-fall-2018": "2018-10-05",
  "pikachu-november-2018": "2018-11-01",
  "pikachu-winter-2018": "2018-12-18",
  "pikachu-feb-2019": "2019-02-26",
  "pikachu-may-2019": "2019-05-07",
  "pikachu-fall-2019": "2019-10-17",
  "pikachu-jan-2020": "2020-01-15", // (month-precise)
  "pikachu-vs-2019": "2020-02-10", // Libre, GO Battle League preseason
  "pikachu-copy-2019": "2020-02-25", // Clone
  "pikachu-costume-2020": "2020-02-25", // red party hat
  "pikachu-safari-2020": "2020-03-27",
  "pikachu-april-2020": "2020-04-09",
  "pikachu-kanto-2020": "2020-05-01",
  "pikachu-johto-2020": "2020-05-08",
  "pikachu-hoenn-2020": "2020-05-15",
  "pikachu-sinnoh-2020": "2020-05-22",
  "pikachu-flying-01": "2020-07-03",
  "pikachu-flying-02": "2020-07-03",
  "pikachu-adventure-hat-2020": "2020-10-01",
  "pikachu-winter-2020": "2020-12-22",
  "pikachu-flying-5th-anniv": "2021-07-06",
  "pikachu-gofest-2021": "2021-07-17",
  "pikachu-rock-star": "2021-07-17",
  "pikachu-pop-star": "2021-07-17",
  "pikachu-halloween-2021": "2021-10-15",
  "pikachu-flying-03": "2022-03-18", // Jeju balloons
  "pikachu-flying-okinawa": "2022-05-03",
  "pikachu-kariyushi": "2022-05-03",
  "pikachu-gofest-2022": "2022-06-04",
  "pikachu-tcg-2022": "2022-06-16",
  "pikachu-anniversary-2022": "2022-07-06",
  "pikachu-wcs-2022": "2022-08-18",
  "pikachu-flying-04": "2022-10-14", // Taipei balloons
  "pikachu-tshirt-01": "2022-10-14",
  "pikachu-tshirt-02": "2022-11-11",
  "pikachu-tshirt-03": "2024-03-05", // Batik shirt, Air Adventures Indonesia
  "pikachu-jan-2023": "2023-01-01",
  "pikachu-gotour-2023-hat": "2023-02-18",
  "pikachu-gotour-2023-bandana": "2023-02-25",
  "pikachu-spring-2023": "2023-04-04",
  "pikachu-jeju": "2023-07-21",
  "pikachu-wcs-2023": "2023-08-11",
  "pikachu-summer-2023-a": "2023-08-26",
  "pikachu-summer-2023-b": "2023-08-26",
  "pikachu-summer-2023-c": "2023-08-26",
  "pikachu-summer-2023-d": "2023-08-26",
  "pikachu-summer-2023-e": "2023-08-26",
  "pikachu-pi": "2023-10-05", // Detective Pikachu Returns
  "pikachu-fall-2023": "2023-10-19",
  "pikachu-holiday-2023": "2023-12-18",
  "pikachu-gotour-2024-a": "2024-02-17",
  "pikachu-gotour-2024-a-02": "2024-02-17",
  "pikachu-gotour-2024-b": "2024-02-17",
  "pikachu-gotour-2024-b-02": "2024-02-17",
  "pikachu-horizons": "2024-03-05",
  "pikachu-gofest-2024-mtiara": "2024-05-30",
  "pikachu-gofest-2024-stiara": "2024-05-30",
  "pikachu-wcs-2024": "2024-08-16",
  "pikachu-kurta": "2024-11-01",
  "pikachu-diwali-2024": "2024-11-01",
  "pikachu-gotour-2025-a": "2025-02-21",
  "pikachu-gotour-2025-a-02": "2025-02-21",
  "pikachu-gotour-2025-b": "2025-02-21",
  "pikachu-gotour-2025-b-02": "2025-02-21",
  "pikachu-doctor": "2025-05-15", // Professor Pikachu, Crown Clash
  "pikachu-gofest-2025-monocle-blue": "2025-05-29",
  "pikachu-gofest-2025-monocle-red": "2025-05-29",
  "pikachu-gofest-2025-monocle-yellow": "2025-05-29",
  "pikachu-gofest-2025-goggles-blue": "2025-06-28",
  "pikachu-gofest-2025-goggles-red": "2025-06-28",
  "pikachu-gofest-2025-goggles-yellow": "2025-06-28",
  "pikachu-indonesia-2025": "2025-08-04",
  "pikachu-wcs-2025": "2025-08-15",
  "pikachu-gotour-2026-a": "2026-02-23",
  "pikachu-gotour-2026-a-02": "2026-02-23",
  "pikachu-gotour-2026-b": "2026-02-24",
  "pikachu-gotour-2026-b-02": "2026-02-25",
  "pikachu-gotour-2026-c": "2026-02-28",
  "pikachu-gotour-2026-c-02": "2026-02-28",
  "pikachu-bb-2026": "2026-04-02",
  "pikachu-visor-2026": "2026-05-12",
  "pikachu-fossil-2026": "2026-05-22",
  "pikachu-gofest-2026-cap-blue": "2026-07-01",
  "pikachu-gofest-2026-cap-red": "2026-07-01",
  "pikachu-gofest-2026-cap-yellow": "2026-07-01",
  "pikachu-anniversary-2026": "2026-07-21",
  "pikachu-pxp-2026": "2026-08-25",
  "pikachu-wcs-2026": "2026-08-28",
  "pikachu-anniversary-2026-malaysia-01": "2026-09-12",
  "pikachu-hanbok-2026": "2026-09-18",
  "pikachu-anniversary-2026-taiwan-01": "2026-09-26", // (month-precise)
  "pikachu-anniversary-2026-singapore-01": "2026-10-01", // (month-precise)
  "pikachu-anniversary-2026-philippine-01": "2026-10-01", // (month-precise)
  "pikachu-astronaut-2026": "2026-10-04",
  "pikachu-halloween-2026": "2026-10-27",
};

/** Month a season or event word in an asset token lands in. */
const TOKEN_MONTHS: ReadonlyArray<[RegExp, number]> = [
  [/\bJAN(UARY)?\b|LUNAR|NEWYEAR|NEW_YEAR/, 1],
  [/\bFEB(RUARY)?\b|GOTOUR|VALENTINE/, 2],
  [/\bMAR(CH)?\b/, 3],
  [/\bAPR(IL)?\b|SPRING|EASTER/, 4],
  [/\bMAY\b/, 5],
  [/\bJUN(E)?\b|GOFEST/, 6],
  [/\bJUL(Y)?\b|SUMMER|ANNIVERSARY/, 7],
  [/\bAUG(UST)?\b|WCS|WORLDS/, 8],
  [/\bSEP(T|TEMBER)?\b/, 9],
  [/\bOCT(OBER)?\b|FALL|AUTUMN|HALLOWEEN/, 10],
  [/\bNOV(EMBER)?\b|DIWALI/, 11],
  [/\bDEC(EMBER)?\b|WINTER|HOLIDAY|XMAS|CHRISTMAS/, 12],
];

/**
 * The asset token of a form's art ("FALL_2019" from "…/pm1.fFALL_2019.icon.png"),
 * or null for art that is not a PokeMiners costume file.
 */
export function costumeAssetToken(spritePath: string): string | null {
  const file = spritePath.split("/").pop() ?? "";
  const m = /^pm\d+\.[cf]([A-Za-z0-9_]+?)(?:\.s)?\.icon\.png$/.exec(file);
  return m ? m[1].toUpperCase() : null;
}

/**
 * A sortable release date for a costume form: the curated table first, else
 * the year and season read off its asset token as the 15th of that month,
 * else null (undated, sorts last).
 */
export function costumeReleaseDate(form: { formId: string; spritePath: string }): string | null {
  const curated = PIKACHU_COSTUME_RELEASED[form.formId];
  if (curated) return curated;
  const token = costumeAssetToken(form.spritePath);
  if (!token) return null;
  const year = /(20[12]\d)/.exec(token)?.[1];
  if (!year) return null;
  // Underscores are the token's word breaks ("JAN_2020"), so they become
  // spaces before the word-boundary tests.
  const words = token.replace(/_/g, " ");
  const month = TOKEN_MONTHS.find(([re]) => re.test(words))?.[1] ?? 6;
  return `${year}-${String(month).padStart(2, "0")}-15`;
}

/**
 * Newest first, dated before undated, ties and the undated in the order they
 * came (a stable sort, so a species' registry order still breaks ties).
 */
export function sortCostumesNewestFirst<T extends { formId: string; spritePath: string }>(
  forms: readonly T[],
): T[] {
  const dated = forms.map((form, index) => ({ form, index, date: costumeReleaseDate(form) }));
  dated.sort((a, b) => {
    if (a.date && b.date && a.date !== b.date) return a.date < b.date ? 1 : -1;
    if (a.date && !b.date) return -1;
    if (!a.date && b.date) return 1;
    return a.index - b.index;
  });
  return dated.map((d) => d.form);
}
